<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { fadeSlide } from "../utils/transition";
  import { circInOut } from "svelte/easing";
  import type { EnquiryFilterConfig, WidgetLayout } from "./services/config";
  import type { TravelDataFormValue, FilterFormValue, PersonalDataFormValue } from "./services/formModels";
  import Form from "../forms/Form.svelte";
  import Button from "../forms/Button.svelte";
  import Input from "../forms/Input.svelte";
  import InputCheckbox from "../forms/InputCheckbox.svelte";
  import type { FormControl } from "../forms/controls";
  import InputSelect from "../forms/InputSelect.svelte";
  import FormActions from "./controls/FormActions.svelte";
  import type { Localizer } from "../l10n/localization.service";
  import type { EnquiryService } from "./services/enquiryService";
  import FormRequired from "./controls/FormRequired.svelte";

  let dispatch = createEventDispatcher<{ submit: string; back: void }>();

  export let travelData: TravelDataFormValue;
  export let filter: FilterFormValue;

  export let form: FormControl<PersonalDataFormValue>;
  export let layout: WidgetLayout;
  export let T: Localizer;
  export let enquiryService: EnquiryService;

  const filterOptions = enquiryService.options.filter || {};

  let loading = false;
  let validationErrors: { key: string; msg: string }[] = [];
  let serverError = false;

  enquiryService.options.trackStep("personal-data");

  async function handleSubmit(e) {
    if (form.validate(true)) {
      if (!loading) {
        validationErrors = [];
        serverError = false;
        try {
          loading = true;

          const result = await enquiryService.saveEnquiry(travelData, filter, form.value);
          if (result.success) {
            dispatch("submit", result.successPage?.html);
          } else {
            validationErrors = result.validationErrors;
          }
        } catch (e) {
          serverError = true;
          console.error(e);
        } finally {
          loading = false;
        }
      }
    }
  }

  function getAccotypeName(accoTypeId: (string | number), filterOptions: EnquiryFilterConfig ) {
    let accoTypeName = filterOptions.accotypes.filter((_) => _.id == accoTypeId).map((_) => _.name)
    return accoTypeName
  }

  function getRegionName(region: (string | number), filterOptions: EnquiryFilterConfig ) {
    let regionName = filterOptions.regions.filter((_) => _.id == region).map((_) => _.name)
    return regionName
  }

  function getResortName(resort: (string | number), filterOptions: EnquiryFilterConfig ) {
    let resortName = filterOptions.resorts.filter((_) => _.id == resort).map((_) => _.name)
    return resortName
  }

  function formatDate(range: [string, string], index: number) {
    const isoDate = range ? range[index] : null;
    return T.format(isoDate, "ddd, dd. MMMM yyyy");
  }
  $: enquiryType = enquiryService.options.enquiryType;
  $: accosCount = enquiryType == 1 ? filter.accosCount : 1;
  $: adultsCount = travelData.rooms?.reduce((c, n) => c + n.adults, 0);
  $: childrenCount = travelData.rooms?.reduce((c, n) => c + n.children, 0);
  $: roomsCount = travelData.rooms?.length;
  $: accoTypes = filter.accotypes.accotypeIds;
  $: regions = filter.areas.regionIds;
  $: resorts = filter.areas.resortIds;

</script>

  <div class="card layout-{layout}">
    <span class="travel-details">
      {#if enquiryType === 1}{@html T.get("personalData.accosCount", accosCount)}<br />{/if}
      <b>{formatDate(travelData.range, 0)} – {formatDate(travelData.range, 1)}</b><br />
      {@html T.get("personalData.adults", adultsCount)},
      {#if childrenCount > 0} {@html T.get("personalData.children", childrenCount)},{/if}
      {@html T.get("personalData.rooms", roomsCount)}<br />
      {#if travelData.hasPets}
        + {T.get("personalData.pets")}<br />
      {/if}<br />
      {#if enquiryType === 1}
      <div class="form-grid-card">
        <div class="accotypes">
          <b>{@html T.get("personalData.accotypes", accoTypes.length)}<br /></b>
          {#each accoTypes as accotype (accotype)}
          {getAccotypeName(accotype, filterOptions)}{#if accotype !== accoTypes[accoTypes.length - 1]}{", "}{/if}
          {/each}
        </div>
        <div class="areas">
          <b>{@html T.get("personalData.areas", regions.length || resorts.length)}<br /></b>
        {#if regions.length > 0}
          {#each regions as region (region)}
          {getRegionName(region, filterOptions)}{#if region !== regions[regions.length - 1]}{", "}{/if}
          {/each}
        {/if}
        {#if regions.length <= 0}
          {#each resorts as resort (resort)}
          {getResortName(resort, filterOptions)}{#if resort !== resorts[resorts.length - 1]}{", "}{/if}
          {/each}
        {/if}
        </div>
      </div>
      {/if}
    </span>
  </div>

<Form bind:form on:submit={handleSubmit}>
  <div class="form-grid">
    <div class="email">
      <Input name="email" type="email" placeholder={T.get("personalData.email")} required />
    </div>

    <div class="title">
      <InputSelect name="title" placeholder={T.get("personalData.salutation")} required>
        <option value="1">{T.get("personalData.salutationOptions.herr")}</option>
        <option value="2">{T.get("personalData.salutationOptions.frau")}</option>
        <option value="3">{T.get("personalData.salutationOptions.familie")}</option>
        <option value="4">{T.get("personalData.salutationOptions.firma")}</option>
      </InputSelect>
    </div>

    <div class="firstname">
      <Input name="firstname" placeholder={T.get("personalData.firstname")} required />
    </div>

    <div class="lastname">
      <Input name="lastname" placeholder={T.get("personalData.lastname")} required />
    </div>

    <div class="phone">
      <Input name="phone" placeholder={T.get("personalData.phone")} />
    </div>

    <div class="message">
      <Input name="message" placeholder={T.get("personalData.message")} rows={5} />
    </div>

    <div class="privacy">
      <InputCheckbox name="privacy" required errors={{ required: T.get("personalData.privacyRequired") }}>{@html T.get("personalData.privacy", enquiryService.options.privacyUrl)} *</InputCheckbox>
    </div>

    <div class="newsletter">
      <InputCheckbox name="newsletter">
        {T.get("personalData.newsletter")}<br />
        <small>{T.get("personalData.newsletterNote")}</small>
      </InputCheckbox>
    </div>
  </div>

  {#if validationErrors.length}
    <ul transition:fadeSlide={{ easing: circInOut }} class="alert scom-alert-danger list-unstyled validation-errors">
      {#each validationErrors as error}
        <li><b>{error.key}:</b> {error.msg}</li>
      {/each}
    </ul>
  {/if}
  {#if serverError}
    <div transition:fadeSlide={{ easing: circInOut }} class="alert scom-alert-danger server-error">{@html T.get("personalData.generalError")}</div>
  {/if}

  <FormRequired {T} />

  <FormActions sticky={layout == "default"}>
    <Button secondary on:click={(_) => dispatch("back")} disabled={loading}>{T.get("personalData.back")}</Button>
    <Button type="submit" disabled={loading}>
      <i class="load-spinner spin" class:spin={loading} />
      {T.get("personalData.submit", filter.accosCount)}
    </Button>
  </FormActions>
</Form>

<style lang="scss">
  @import "./_mixins.scss";

  .card {
    background-color: #EBEBEB;
    padding: 1.375rem;
    overflow-wrap: break-word;
    margin-bottom: 2rem;

    &.layout-simple {
      background-color: #f7f7f7;
      padding: 0 0 0 1.25rem ;
      margin-bottom: 1rem;
    }

    .form-grid-card {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    .accotypes {
      padding: 0 1.375rem 0 0;
      border-right: 1px solid #000;
      overflow: auto;
    }

    .areas {
      padding: 0 0 0 1.375rem ;
      overflow: auto;
    }

    .travel-details {
      font-size: 15px;
    }
  }
  
  .form-grid {
    display: grid;
    grid-template:
      "email title" auto
      "firstname lastname" auto
      "phone message" auto
      "privacy message" auto
      "newsletter newsletter" auto / 1fr 1fr;
    gap: 1.2rem;

    @include media-breakpoint-mobile {
      grid-template:
        "email" auto
        "title" auto
        "firstname" auto
        "lastname" auto
        "phone" auto
        "message" auto
        "privacy" auto
        "newsletter" auto / 100%;
    }

    .email {
      grid-area: email;
    }
    .title {
      grid-area: title;
    }
    .firstname {
      grid-area: firstname;
    }
    .lastname {
      grid-area: lastname;
    }
    .phone {
      grid-area: phone;
    }
    .message {
      grid-area: message;
    }
    .privacy {
      grid-area: privacy;
    }
    .newsletter {
      grid-area: newsletter;
    }
  }
  
  .validation-errors,
  .server-error {
    margin-top: 1em;
    border-radius: 28px;
    border-width: 2px;
  }
</style>
